import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import extraClassesService from '../../services/extraClassesService';
import StudentAssignmentSolver from './StudentAssignmentSolver';
import { CheckCircle, X, FileText, Clock } from '@phosphor-icons/react';

function StudentExtraClassesSection() {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignmentsByClass, setAssignmentsByClass] = useState({});
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [activeExtraClassId, setActiveExtraClassId] = useState(null);
  const [submissionText, setSubmissionText] = useState('');
  const [submittingId, setSubmittingId] = useState(null);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [showSolver, setShowSolver] = useState(false);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);

  const statusBadgeMap = {
    pending: { label: 'Pending', bg: '#fef3c7', color: '#92400e' },
    approved: { label: 'Approved', bg: '#dcfce7', color: '#166534' },
    rejected: { label: 'Rejected', bg: '#fee2e2', color: '#991b1c' },
    active: { label: 'Active', bg: '#dbeafe', color: '#1d4ed8' },
    completed: { label: 'Completed', bg: '#ecfccb', color: '#365314' },
  };

  const pageStyles = {
    wrapper: { width: '100%', maxWidth: 1040, margin: '0 auto', padding: '16px' },
    header: { marginBottom: 24 },
    title: { fontSize: 28, margin: 0, color: '#111827' },
    subtitle: { marginTop: 8, color: '#475569', lineHeight: 1.6 },
    emptyState: { padding: 24, borderRadius: 20, background: '#f8fafc', border: '1px solid #e2e8f0', textAlign: 'center', color: '#475569' },
    classCard: { background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 24, boxShadow: '0 18px 50px rgba(15, 23, 42, 0.06)', padding: 24, marginBottom: 20 },
    classHeader: { display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' },
    classTitle: { margin: 0, fontSize: 22, color: '#111827' },
    classSubtitle: { margin: '10px 0 0', color: '#6b7280', lineHeight: 1.7 },
    classMeta: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 20, color: '#475569' },
    metaItem: { background: '#f8fafc', borderRadius: 16, padding: 14, border: '1px solid #e2e8f0' },
    sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' },
    sectionTitle: { margin: 0, fontSize: 16, color: '#0f172a' },
    sectionMeta: { color: '#6b7280', fontSize: 13 },
    assignmentCard: { background: '#f8fafc', borderRadius: 18, border: '1px solid #e2e8f0', padding: 18, marginBottom: 14 },
    assignmentActionRow: { display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' },
    buttonPrimary: { padding: '10px 16px', borderRadius: 14, border: 'none', background: '#2563eb', color: '#ffffff', cursor: 'pointer' },
    buttonSecondary: { padding: '10px 16px', borderRadius: 14, border: '1px solid #cbd5e1', background: '#ffffff', color: '#334155', cursor: 'pointer' },
    statusBadge: { display: 'inline-flex', alignItems: 'center', borderRadius: 999, padding: '7px 14px', fontSize: 13, fontWeight: 700 },
  };

  const getEnrollmentStatusBadge = (status) => {
    const normalized = String(status || 'pending').toLowerCase();
    return statusBadgeMap[normalized] || statusBadgeMap.pending;
  };

  const loadAssignments = async (extraClassId) => {
    if (!extraClassId) return;
    try {
      const data = await extraClassesService.listAssignments(extraClassId);
      setAssignmentsByClass((prev) => ({ ...prev, [extraClassId]: data.assignments || [] }));
    } catch (err) {
      toast.error(err.message || 'Unable to load assignments');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await extraClassesService.getStudentEnrollments();
        const loadedEnrollments = data.enrollments || [];
        setEnrollments(loadedEnrollments);
        await Promise.all(loadedEnrollments.map((item) => loadAssignments(item.extra_class?.id)));
      } catch (err) {
        toast.error(err.message || 'Failed to load your classes');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const openSubmissionModal = (extraClassId, assignment) => {
    setActiveExtraClassId(extraClassId);
    setActiveAssignment(assignment);
    setSubmissionText('');
    setSubmissionSuccess(false);
  };

  const openQuestionSolver = (extraClassId, assignment) => {
    setActiveExtraClassId(extraClassId);
    setSelectedAssignmentId(assignment.id);
    setShowSolver(true);
  };

  const closeSolver = () => {
    setShowSolver(false);
    setSelectedAssignmentId(null);
    setActiveExtraClassId(null);
  };

  const closeSubmissionModal = () => {
    setActiveAssignment(null);
    setActiveExtraClassId(null);
    setSubmissionText('');
    setSubmissionSuccess(false);
  };

  const getAssignmentStatus = (assignment) => {
    if (assignment.graded) return 'graded';
    if (assignment.status === 'late') return 'late';
    if (assignment.status === 'submitted' || assignment.submitted_at) return 'submitted';
    const dueDate = assignment.due_date ? new Date(assignment.due_date) : null;
    if (dueDate && dueDate < new Date()) return 'overdue';
    return 'pending';
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const normalized = dateString.replace(' ', 'T');
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatStatusBadge = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'graded') return { label: 'Graded', bg: '#ecfdf5', color: '#166534' };
    if (normalized === 'submitted') return { label: 'Submitted', bg: '#eff6ff', color: '#1d4ed8' };
    if (normalized === 'overdue') return { label: 'Overdue', bg: '#fef2f2', color: '#b91c1c' };
    return { label: 'Pending', bg: '#f8fafc', color: '#134e4a' };
  };

  const renderStatusBadge = (status) => {
    const badge = getEnrollmentStatusBadge(status);
    return (
      <span style={{ ...pageStyles.statusBadge, background: badge.bg, color: badge.color }}>
        {badge.label}
      </span>
    );
  };

  const handleSubmitAssignment = async (e) => {
    e.preventDefault();
    if (!activeAssignment || !activeExtraClassId) return;

    try {
      setSubmittingId(activeAssignment.id);
      await extraClassesService.submitAssignment(activeExtraClassId, activeAssignment.id, {
        submission_text: submissionText,
      });
      setSubmissionSuccess(true);
      toast.success('Assignment submitted');
    } catch (err) {
      toast.error(err.message || 'Unable to submit assignment');
    } finally {
      setSubmittingId(null);
    }
  };

  const renderAssignmentCard = (assignment, extraClassId) => {
    const status = getAssignmentStatus(assignment);
    const badge = formatStatusBadge(status);
    const isFinalized = status === 'submitted' || status === 'graded' || status === 'late';
    return (
      <div key={assignment.id} style={pageStyles.assignmentCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <FileText size={18} />
              <strong style={{ fontSize: 16, color: '#111827' }}>{assignment.title}</strong>
            </div>
            <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.7, marginBottom: 10 }}>{assignment.instructions || assignment.description || 'No assignment details available.'}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', color: '#64748b', fontSize: 13 }}>
              <span>Due: {formatDate(assignment.due_date) || 'No due date'}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 999, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: badge.color, display: 'inline-block' }} />
                {badge.label}
              </span>
            </div>
          </div>
          <div style={pageStyles.assignmentActionRow}>
            {!isFinalized && assignment.questions_count > 0 && (
              <button onClick={() => openQuestionSolver(extraClassId, assignment)} style={pageStyles.buttonSecondary}>
                Answer Questions
              </button>
            )}
            {!isFinalized ? (
              <button onClick={() => openSubmissionModal(extraClassId, assignment)} style={pageStyles.buttonPrimary}>
                Submit
              </button>
            ) : (
              <button disabled style={{ ...pageStyles.buttonSecondary, background: '#f8fafc', color: '#64748b', cursor: 'not-allowed' }}>
                {status === 'graded' ? 'Already graded' : status === 'submitted' ? 'Already submitted' : 'Late submission'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={pageStyles.wrapper}>
      <div style={pageStyles.header}>
        <h2 style={pageStyles.title}>My Extra Classes</h2>
        <p style={pageStyles.subtitle}>View your approved lessons, join details, and shared assignments in one place.</p>
      </div>

      {loading ? (
        <div style={pageStyles.emptyState}><p>Loading your extra classes…</p></div>
      ) : enrollments.length === 0 ? (
        <div style={pageStyles.emptyState}>
          <p>No extra class registrations found yet.</p>
          <p style={{ marginTop: 8 }}>Once a parent registers you, your approved class details and assignments will appear here.</p>
        </div>
      ) : (
        enrollments.map((item) => {
          const statusBadge = renderStatusBadge(item.status);
          const assignments = assignmentsByClass[item.extra_class?.id] || [];
          return (
            <div key={item.id} style={pageStyles.classCard}>
              <div style={pageStyles.classHeader}>
                <div>
                  <h3 style={pageStyles.classTitle}>{item.extra_class?.title || 'Extra Class'}</h3>
                  <p style={pageStyles.classSubtitle}>{item.extra_class?.description || 'This class helps support your learning outside regular lessons.'}</p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-end' }}>
                  {statusBadge}
                  <span style={{ color: '#6b7280', fontSize: 13 }}>{item.extra_class?.meeting_link ? 'Live link available' : 'Meeting link will be shared'}</span>
                </div>
              </div>

              <div style={pageStyles.classMeta}>
                <div style={pageStyles.metaItem}>
                  <span style={{ display: 'block', marginBottom: 6, fontSize: 12, color: '#9ca3af' }}>Teacher</span>
                  <span>{item.extra_class?.teacher_name || item.teacher_name || 'Assigned teacher'}</span>
                </div>
                <div style={pageStyles.metaItem}>
                  <span style={{ display: 'block', marginBottom: 6, fontSize: 12, color: '#9ca3af' }}>Meeting details</span>
                  {item.extra_class?.meeting_link ? (
                    <a
                      href={item.extra_class.meeting_link}
                      target="_blank"
                      rel="noreferrer noopener"
                      style={{ color: '#2563eb', fontWeight: 600, wordBreak: 'break-all' }}
                    >
                      Join class
                    </a>
                  ) : (
                    <span>To be shared</span>
                  )}
                </div>
                <div style={pageStyles.metaItem}>
                  <span style={{ display: 'block', marginBottom: 6, fontSize: 12, color: '#9ca3af' }}>Billing status</span>
                  <span>{item.billing_status || 'Pending'}</span>
                </div>
                <div style={pageStyles.metaItem}>
                  <span style={{ display: 'block', marginBottom: 6, fontSize: 12, color: '#9ca3af' }}>Next due date</span>
                  <span>{formatDate(item.next_due_date) || 'N/A'}</span>
                </div>
              </div>

              <div style={{ marginTop: 24 }}>
                <div style={pageStyles.sectionHeader}>
                  <h4 style={pageStyles.sectionTitle}>Assignments</h4>
                  <span style={pageStyles.sectionMeta}>{assignments.length} assignment{assignments.length !== 1 ? 's' : ''}</span>
                </div>

                {assignments.length === 0 ? (
                  <div style={pageStyles.emptyState}>No assignments have been shared for this class yet.</div>
                ) : (
                  assignments.map((assignment) => renderAssignmentCard(assignment, item.extra_class?.id))
                )}
              </div>
            </div>
          );
        })
      )}

      {showSolver && selectedAssignmentId && activeExtraClassId && (
        <StudentAssignmentSolver
          assignmentId={selectedAssignmentId}
          extraClassId={activeExtraClassId}
          onClose={closeSolver}
          onSubmissionComplete={() => {
            closeSolver();
            loadAssignments(activeExtraClassId);
          }}
        />
      )}
      {activeAssignment && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ width: '100%', maxWidth: 640, background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 24px 60px rgba(15, 23, 42, 0.12)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <div>
                <h2 style={{ margin: 0 }}>{activeAssignment.title}</h2>
                <div style={{ marginTop: 6, color: '#64748b', fontSize: 13 }}>{activeAssignment.instructions || activeAssignment.description}</div>
              </div>
              <button onClick={closeSubmissionModal} style={{ border: 'none', background: 'transparent', color: '#475569', cursor: 'pointer' }}><X size={24} /></button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 14, background: '#f8fafc', borderRadius: 14, border: '1px solid #e2e8f0', marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Clock size={18} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Due date</div>
                  <div style={{ fontSize: 13, color: '#475569' }}>{activeAssignment.due_date || 'No due date'}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#0f172a' }}>
                <CheckCircle size={18} weight="fill" />
                <span style={{ fontSize: 13, fontWeight: 600 }}>Submission</span>
              </div>
            </div>

            {submissionSuccess ? (
              <div style={{ padding: 16, background: '#ecfdf5', borderRadius: 14, border: '1px solid #bbf7d0', color: '#166534', display: 'flex', alignItems: 'center', gap: 10 }}>
                <CheckCircle size={18} weight="fill" />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>Submission recorded</div>
                  <div style={{ fontSize: 13, color: '#14532d' }}>Your teacher will review it shortly.</div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmitAssignment} style={{ display: 'grid', gap: 14 }}>
                <textarea
                  value={submissionText}
                  onChange={(e) => setSubmissionText(e.target.value)}
                  rows={10}
                  placeholder="Type your answer or notes here"
                  style={{ width: '100%', minHeight: 200, padding: 14, borderRadius: 14, border: '1px solid #cbd5e1', fontSize: 14, resize: 'vertical' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <button type="button" onClick={closeSubmissionModal} style={{ padding: '12px 18px', borderRadius: 14, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button type="submit" disabled={submittingId === activeAssignment.id} style={{ padding: '12px 18px', borderRadius: 14, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer' }}>
                    {submittingId === activeAssignment.id ? 'Submitting...' : 'Submit Assignment'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentExtraClassesSection;
